import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../config/api'
import { getUserDisplayName as formatUserDisplayNameFromUser } from '../utils/displayName'
import { useProfilePhoto } from './ProfilePhotoContext'
import { ToastContainer } from './Toast'
import './Header.css'

const Profile = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  })
  const [toasts, setToasts] = useState([])
  const { profilePhotoUrl, setProfilePhotoUrl } = useProfilePhoto()
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  
  const profileModalRef = useRef(null)
  const fileInputRef = useRef(null)

  // Toast helper functions
  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random()
    setToasts(prev => [...prev, { id, message, type, duration }])
  }

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }

  // Load user from API when modal opens (never show placeholder demo identity).
  useEffect(() => {
    if (!isOpen) return undefined

    let cancelled = false

    const loadUser = async () => {
      try {
        const res = await fetch(getApiUrl('/api/users/me'), { credentials: 'include' })
        if (res.ok) {
          const json = await res.json()
          if (!cancelled && json?.success && json?.data) {
            setUser(json.data)
            localStorage.setItem('user', JSON.stringify(json.data))
            return
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error)
      }

      if (cancelled) return
      try {
        const userStr = localStorage.getItem('user')
        setUser(userStr ? JSON.parse(userStr) : null)
      } catch (error) {
        console.error('Error loading user data:', error)
        setUser(null)
      }
    }

    loadUser()
    return () => { cancelled = true }
  }, [isOpen])

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsEditingProfile(false)
      // Initialize form data with current user data
      if (user) {
        setProfileFormData({
          firstName: user.first_name || user.firstName || '',
          lastName: user.last_name || user.lastName || '',
          phone: user.phone || user.phone_number || ''
        })
      }
    }
  }, [isOpen, user])

  // Fetch profile photo URL when modal opens and user is set (updates context so sidebar reflects it)
  useEffect(() => {
    if (!isOpen || !user?.id) return
fetch(getApiUrl('/api/user/profile-photo-url'), {
      headers: { } })
      .then((res) => res.json())
      .then((data) => setProfilePhotoUrl(data.success && data.url ? data.url : null))
      .catch(() => setProfilePhotoUrl(null))
  }, [isOpen, user?.id, setProfilePhotoUrl])

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileModalRef.current && !profileModalRef.current.contains(event.target) && isOpen) {
        if (event.target.classList.contains('valuation-profile-modal-overlay')) {
          onClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Helper functions
  const getUserDisplayName = () => {
    if (user) {
      const formatted = formatUserDisplayNameFromUser(user)
      if (formatted) return formatted
      if (user.name) return user.name
    }
    return 'Your account'
  }

  const getUserEmail = () => {
    if (!user) return '—'
    return user.email || user.googleEmail || '—'
  }


  // Handle profile edit
  const handleEditProfile = () => {
    setIsEditingProfile(true)
    // Initialize form data with current user data
    if (user) {
      setProfileFormData({
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        phone: user.phone || user.phone_number || ''
      })
    }
  }

  // Handle profile save
  const handleSaveProfile = async () => {
    setIsSavingProfile(true)
    try {


      const endpoint = `/api/users/me`
      const response = await fetch(getApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: profileFormData.firstName,
          lastName: profileFormData.lastName,
          phone: profileFormData.phone
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // Update local user data
        const updatedUser = {
          ...user,
          first_name: profileFormData.firstName,
          firstName: profileFormData.firstName,
          last_name: profileFormData.lastName,
          lastName: profileFormData.lastName,
          phone: profileFormData.phone,
          phone_number: profileFormData.phone
        }
        setUser(updatedUser)
        localStorage.setItem('user', JSON.stringify(updatedUser))
        setIsEditingProfile(false)
        alert('Profile updated successfully!')
      } else {
        alert(result.message || 'Failed to update profile. Please try again.')
      }
    } catch (error) {
      console.error('[Profile] Error updating profile:', error)
      alert('An error occurred while updating your profile. Please try again.')
    } finally {
      setIsSavingProfile(false)
    }
  }

  // Handle profile cancel
  const handleCancelEdit = () => {
    setIsEditingProfile(false)
    // Reset form data to current user data
    if (user) {
      setProfileFormData({
        firstName: user.first_name || user.firstName || '',
        lastName: user.last_name || user.lastName || '',
        phone: user.phone || user.phone_number || ''
      })
    }
  }

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      showToast('Please choose a JPG or PNG image', 'error')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be under 5MB', 'error')
      return
    }
    setUploadingPhoto(true)
    const formData = new FormData()
    formData.append('file', file)
    fetch(getApiUrl('/api/user/profile-photo'), {
      method: 'POST',
      headers: { },
      body: formData })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.url) {
          setProfilePhotoUrl(data.url)
          showToast('Profile photo updated')
        } else {
          showToast(data.detail || 'Upload failed', 'error')
        }
      })
      .catch(() => showToast('Upload failed', 'error'))
      .finally(() => {
        setUploadingPhoto(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
      })
  }

  if (!isOpen) return null

  return (
    <div className="valuation-profile-modal-overlay" onClick={onClose}>
      <div className="valuation-profile-modal" ref={profileModalRef} onClick={(e) => e.stopPropagation()}>
        <div className="valuation-profile-modal-header">
          <h2>My Profile</h2>
          <button 
            className="valuation-profile-modal-close"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="valuation-profile-modal-content">
          {/* Profile Information Section */}
          <div className="valuation-profile-section">
            <div className="valuation-profile-section-header">
              <h3 className="valuation-profile-section-title">Profile Information</h3>
              {!isEditingProfile && (
                <button 
                  className="valuation-profile-edit-button"
                  onClick={handleEditProfile}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
              )}
            </div>
            {/* Profile photo (user) */}
            <div className="valuation-profile-photo-row">
              <div className="valuation-profile-avatar-wrap">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" className="valuation-profile-avatar-img" />
                ) : (
                  <div className="valuation-profile-avatar-initial">
                    {(getUserDisplayName() || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  className="valuation-profile-photo-input"
                  onChange={handleProfilePhotoChange}
                  disabled={uploadingPhoto}
                  aria-label="Upload profile photo"
                />
                <button
                  type="button"
                  className="valuation-profile-upload-photo"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? 'Uploading…' : 'Upload photo'}
                </button>
              </div>
            </div>
            <div className="valuation-profile-info-grid">
              {isEditingProfile ? (
                <>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label" htmlFor="profile-first-name">First Name</label>
                    <input
                      id="profile-first-name"
                      type="text"
                      className="valuation-profile-input"
                      value={profileFormData.firstName}
                      onChange={(e) => setProfileFormData({ ...profileFormData, firstName: e.target.value })}
                      placeholder="First Name"
                    />
                  </div>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label" htmlFor="profile-last-name">Last Name</label>
                    <input
                      id="profile-last-name"
                      type="text"
                      className="valuation-profile-input"
                      value={profileFormData.lastName}
                      onChange={(e) => setProfileFormData({ ...profileFormData, lastName: e.target.value })}
                      placeholder="Last Name"
                    />
                  </div>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label" htmlFor="profile-phone">Phone</label>
                    <input
                      id="profile-phone"
                      type="tel"
                      className="valuation-profile-input"
                      value={profileFormData.phone}
                      onChange={(e) => setProfileFormData({ ...profileFormData, phone: e.target.value })}
                      placeholder="Phone Number"
                    />
                  </div>
                  <div className="valuation-profile-info-item valuation-profile-actions">
                    <button
                      className="valuation-profile-save-button"
                      onClick={handleSaveProfile}
                      disabled={isSavingProfile}
                    >
                      {isSavingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      className="valuation-profile-cancel-button"
                      onClick={handleCancelEdit}
                      disabled={isSavingProfile}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label">Name</label>
                    <div className="valuation-profile-info-value">{getUserDisplayName()}</div>
                  </div>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label">Email</label>
                    <div className="valuation-profile-info-value">{getUserEmail()}</div>
                  </div>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label">Phone</label>
                    <div className="valuation-profile-info-value">
                      {user && (user.phone || user.phone_number) ? (user.phone || user.phone_number) : 'Not provided'}
                    </div>
                  </div>
                  <div className="valuation-profile-info-item">
                    <label className="valuation-profile-info-label">Role</label>
                    <div className="valuation-profile-info-value">
                      {user && user.role ? (
                        <span className="profile-role-badge" data-role={user.role.toLowerCase()}>
                          {(user.role.toLowerCase() === 'seller' || user.role.toLowerCase() === 'smb') ? 'Small Business Owner' : user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase()}
                        </span>
                      ) : 'Not specified'}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  )
}

export default Profile

